import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
let scriptLoadingPromise = null;

function loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve();
    if (scriptLoadingPromise) return scriptLoadingPromise;

    scriptLoadingPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Turnstile script'));
        document.head.appendChild(script);
    });
    return scriptLoadingPromise;
}

/**
 * Cloudflare Turnstile widget. Exposes `reset()` via ref since tokens are single-use.
 */
const Turnstile = forwardRef(function Turnstile({ onVerify, onExpire, onError }, ref) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);

    useImperativeHandle(ref, () => ({
        reset: () => {
            if (widgetIdRef.current !== null) window.turnstile?.reset(widgetIdRef.current);
        },
    }));

    useEffect(() => {
        let cancelled = false;

        if (!process.env.REACT_APP_TURNSTILE_SITE_KEY) {
            console.error('Turnstile: REACT_APP_TURNSTILE_SITE_KEY was not set at build time');
            onError?.();
            return undefined;
        }

        loadTurnstileScript().then(() => {
            if (cancelled || !containerRef.current) return;
            widgetIdRef.current = window.turnstile.render(containerRef.current, {
                sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
                appearance: 'interaction-only',
                callback: (token) => onVerify?.(token),
                'expired-callback': () => onExpire?.(),
                'error-callback': () => onError?.(),
            });
        }).catch((err) => {
            console.error('Turnstile load failed:', err);
            onError?.();
        });

        return () => {
            cancelled = true;
            if (widgetIdRef.current !== null) window.turnstile?.remove(widgetIdRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <div ref={containerRef} />;
});

export default Turnstile;
